<template>
  <div class="flex flex-col h-full min-w-0">
    <div
      class="flex flex-wrap sm:flex-nowrap items-center justify-between my-4 lg:my-8"
    >
      <!-- title -->
      <div class="task-1 flex-grow flex items-center">
        <svg-icon
          type="mdi"
          :path="icons.tasks"
          :size="32"
          class="text-navy"
        ></svg-icon>

        <span class="ml-2 text-navy font-bold leading-7 text-2xl">
          View Task
        </span>
      </div>

      <!-- Back to Task -->
      <div class="task-2 sm:task-3 sm:mr-3">
        <inertia-link
          class="flex items-center justify-center w-7 h-7 rounded-full bg-navy hover:bg-navy-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-light"
          :href="route('app.task.index')"
        >
          <svg-icon
            type="mdi"
            :path="icons.return"
            :size="20"
            class="text-white"
          ></svg-icon>
          <span class="sr-only"> Task list </span>
        </inertia-link>
      </div>
    </div>

    <flash-message />

    <!-- Consolidated Task Header -->
    <div class="bg-white w-full border-b border-navy-light">
      <div class="overflow-x-auto">
        <div class="flex flex-wrap items-start p-4 gap-x-6 gap-y-4">
        <div class="min-w-0" style="width: 280px; max-width: 100%;">
          <div class="flex items-center gap-2">
            <h3 class="text-navy font-bold text-xl">{{ task.farmer_name }}</h3>
            <inertia-link
              :href="route('app.farmer.view', {
                farmer: farmer_id,
                tab: 'farms',
                showPaths: task.farmteam_task_number,
              })"
              class="inline-flex items-center justify-center w-6 h-6 rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-500 hover:text-indigo-600"
              title="Open Farm Manager"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
                <path d="M7 17L17 7" />
                <path d="M7 7h10v10" />
              </svg>
              <span class="sr-only">Open Farm Manager</span>
            </inertia-link>
          </div>
          <div class="text-sm text-gray-600 mt-1">
            Task #{{ task.farmteam_task_number }}
          </div>
          <div class="text-[13px] leading-5 text-gray-700 whitespace-pre-line mt-2">
            {{ task.farmer_address }}
          </div>
        </div>
        
        <div class="flex flex-col text-[13px] min-w-0" style="width: 220px; max-width: 100%; line-height: 1.4;">
          <div class="min-h-[1.4em] flex items-start py-0.5" v-if="$page.props.auth.user.role == 'admin'"><strong>Advisor:</strong>&nbsp;{{ task.advisor || 'Not Set' }}</div>
          <div class="min-h-[1.4em] flex items-start py-0.5"><strong>Soil Sampler:</strong>&nbsp;{{ task.soil_sampler || 'Not Set' }}</div>
          <div class="min-h-[1.4em] flex items-start py-0.5"><strong>Scheduled From:</strong>&nbsp;{{ task.date || 'Not Set' }}</div>
          <div class="min-h-[1.4em] flex items-start py-0.5" v-if="task.complete_by"><strong>Scheduled To:</strong>&nbsp;{{ task.complete_by }}</div>
          <div class="min-h-[1.4em] flex items-start py-0.5"><strong>Required Samples:</strong>&nbsp;{{ task.no_of_samples || 'Not Set' }}</div>
          <div class="min-h-[1.4em] flex items-start py-0.5" v-if="task.issues_approve_by"><strong>Approved By:</strong>&nbsp;{{ task.issues_approve_by }}</div>
        </div>
        
        <div class="flex flex-col text-[13px] min-w-0" style="width: 240px; max-width: 100%; line-height: 1.4;">
          <div class="min-h-[1.4em] flex items-start py-0.5" v-if="$page.props.auth.user.role == 'admin'">
            <strong class="flex-shrink-0">Farms:</strong>&nbsp;
            <span class="break-words">{{ task.farms || 'Not Set' }}</span>
          </div>
          <div class="min-h-[1.4em] flex items-center gap-2 py-0.5">
            <strong>Status:</strong> 
            <span class="px-2 py-1 rounded-full text-xs font-medium" :class="getStatusClass(task.status)">
              {{ task.status || 'Not Set' }}
            </span>
          </div>
          <div class="min-h-[1.4em] flex items-center gap-2 py-0.5">
            <strong>Method:</strong> 
            <span class="px-2 py-1 rounded text-xs font-medium" :class="collectionMethodClass">
              {{ collectionMethodText }}
            </span>
          </div>
        </div>
        
        <div class="flex flex-col text-[13px] min-w-0" style="width: 200px; max-width: 100%; line-height: 1.4;">
          <div class="min-h-[1.4em] flex items-start py-0.5"><strong>Lab:</strong>&nbsp;{{ task.lab || 'Not Set' }}</div>
          <div class="min-h-[1.4em] flex items-center gap-2 py-0.5">
            <strong>Lab Status:</strong> 
            <span class="px-2 py-1 rounded-full text-xs font-medium" :class="labStatusClass">
              {{ labStatusInfo || 'N/A' }}
            </span>
          </div>
          <div class="min-h-[1.4em] flex items-start py-0.5">
            <strong>Sent to Lab:</strong>&nbsp;{{ sentToLabInfo || 'Not Sent' }}
          </div>
        </div>
        
        <div class="flex items-start gap-2 ml-auto">
          <div class="relative" @keydown.escape="showActions=false">
            <button @click.stop="toggleActions" class="w-9 h-9 mt-0 flex items-center justify-center rounded-full border border-gray-300 bg-white hover:bg-gray-50 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" :aria-expanded="showActions.toString()" aria-haspopup="true">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4 text-gray-600"><circle cx="12" cy="5" r="1" /><circle cx="12" cy="12" r="1" /><circle cx="12" cy="19" r="1" /></svg>
              <span class="sr-only">Task actions</span>
            </button>
            <transition name="fade">
              <div v-if="showActions" class="absolute right-0 mt-2 w-44 bg-white border border-gray-200 rounded-md shadow-lg z-20 py-1 text-sm">
                <inertia-link v-if="canEditTask" :href="route('app.task.edit', task.id)" class="block px-3 py-2 hover:bg-gray-50">Edit Task</inertia-link>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>
  </div>

    <div class="bg-gradient-to-b from-gray-50 to-white">
      <nav class="flex w-full overflow-auto px-2 pt-2" aria-label="Tabs">
        <!-- Records -->
        <a
          v-on:click.stop="tab = 'records'"
          :class="tabClass('records')"
          class="inline-flex items-center px-4 py-2 text-sm font-medium rounded-t-md border-b-2 cursor-pointer transition-colors"
        >
          <svg-icon type="mdi" :path="icons.taskRecords" :size="16" class="mr-2"></svg-icon>
          <span>Records ({{ recordsCount }})</span>
        </a>

        <!-- Comments -->
        <a
          v-on:click.stop="tab = 'comments'"
          :class="tabClass('comments')"
          class="inline-flex items-center px-4 py-2 text-sm font-medium rounded-t-md border-b-2 cursor-pointer transition-colors"
        >
          <svg-icon type="mdi" :path="icons.taskComments" :size="16" class="mr-2"></svg-icon>
          <span>Comments ({{ commentCount }})</span>
        </a>

        <!-- Actions -->
        <a
          v-on:click.stop="tab = 'actions'"
          :class="tabClass('actions')"
          class="inline-flex items-center px-4 py-2 text-sm font-medium rounded-t-md border-b-2 cursor-pointer transition-colors"
        >
          <svg-icon type="mdi" :path="icons.taskActions" :size="16" class="mr-2"></svg-icon>
          <span>Lab</span>
        </a>

        <!-- Reports -->
        <a
          v-on:click.stop="tab = 'reports'"
          :class="tabClass('reports')"
          class="inline-flex items-center px-4 py-2 text-sm font-medium rounded-t-md border-b-2 cursor-pointer transition-colors"
        >
          <svg-icon type="mdi" :path="icons.taskReports" :size="16" class="mr-2"></svg-icon>
          <span>Reports</span>
        </a>

        <!-- Charts -->
        <a
          v-on:click.stop="tab = 'charts'"
          :class="tabClass('charts')"
          class="inline-flex items-center px-4 py-2 text-sm font-medium rounded-t-md border-b-2 cursor-pointer transition-colors"
        >
          <svg-icon type="mdi" :path="icons.taskCharts" :size="16" class="mr-2"></svg-icon>
          <span>Charts</span>
        </a>
      </nav>
      <div class="h-[1px] w-full bg-gray-200"></div>
    </div>

    <div v-show="tab == 'records'" class="flex-grow min-w-0 overflow-x-clip xs:!w-full">
      <task-records
        :task="task"
        :farmteam_task_number="task.farmteam_task_number"
        :samples="samples"
        :farmer_id="farmer_id"
        :tests="tests"
        :availableLabTests="availableLabTests"
        :currentTaskLabId="getTaskLabId"
        :currentTab="tab"
        :labUpdateChanges="labUpdateChanges"
        :labUpdateNeeded="labUpdateNeeded"
        @test-sent-to-lab="updateTestSentToLab"
      />
    </div>

    <div v-show="tab == 'actions'" class="flex-grow min-w-0 overflow-x-clip xs:!w-full">
      <task-actions :tests="task.tests" :task="task" :currentTab="tab" :lab-update-needed="labUpdateNeeded" :lab-update-changes="labUpdateChanges" :available-lab-certificates="availableLabCertificates" />
    </div>

    <div v-show="tab == 'comments'" class="flex-grow min-w-0 overflow-x-clip xs:!w-full">
      <task-comments :task="task" :tests="tests" />
    </div>

    <div v-show="tab == 'reports'" class="flex-grow min-w-0 overflow-x-clip xs:!w-full">
      <task-reports :task="task" :tests="tests" :available-lab-certificates="availableLabCertificates" />
    </div>

    <div v-show="tab == 'charts'" class="flex-grow min-w-0 overflow-x-clip xs:!w-full">
      <soil-analysis-charts
        :chartDetails="chartDetails"
        :task="task.id"
        :farm="getFirstFarmId"
        :chartLegend="chartLegend"
      />
    </div>
  </div>
</template>

<script>
import { showDafmLpis } from "@/Partials/Map/Util/DafmLpisPage";
import AppLayout from "@/Layouts/App";
import _ from "lodash";
import TaskDetails from "@/Pages/Task/Partials/Details";
import TaskRecords from "@/Pages/Task/Partials/Records";
import TaskActions from "@/Pages/Task/Partials/Actions";
import TaskReports from "@/Pages/Task/Partials/Reports";
import TaskComments from "@/Pages/Task/Partials/Comments";
import SoilAnalysisCharts from "@/Pages/SoilAnalysis/Partials/Charts";
import {
  mdiInformationOutline,
  mdiClipboardTextMultipleOutline,
  mdiClipboardTextOutline,
  mdiArrowLeft,
  mdiFileCogOutline,
  mdiClipboardEditOutline,
  mdiChevronLeft,
  mdiChevronRight,
  mdiTestTubeEmpty,
  mdiCommentTextOutline,
  mdiChartBoxMultipleOutline,
} from "@mdi/js";

export default {
  layout: [AppLayout],
  components: {
    TaskDetails,
    TaskRecords,
    TaskActions,
    TaskReports,
    TaskComments,
    SoilAnalysisCharts,
  },

  props: {
    farmer_id: String,
    farmer: Object,
    landing_tab: String,
    task: Object,
    tests: Array,
    samples: Array,
    data: Array,
    availableLabTests: Array,
    labUpdateChanges: Object,
    labUpdateNeeded: Boolean,
    availableLabCertificates: Array,
    chartDetails: Object,
    chartLegend: Array,
  },

  data() {
    return {
      tab: (this.landing_tab && this.landing_tab !== '') ? this.landing_tab : 'records', // Default to records tab
      showActions: false,
      icons: {
        taskDetail: mdiInformationOutline,
        taskRecords: mdiClipboardTextMultipleOutline,
        tasks: mdiClipboardTextOutline,
        return: mdiArrowLeft,
        taskActions: mdiTestTubeEmpty,
        taskComments: mdiCommentTextOutline,
        taskReports: mdiClipboardEditOutline,
        taskCharts: mdiChartBoxMultipleOutline,
        leftIcon: mdiChevronLeft,
        rightIcon: mdiChevronRight,
      },
    };
  },
  mounted() {
    // Hide task pins from the main tasks table when viewing individual task
    this.$emitter.emit('hideTaskPins');
    
    // Use the pre-loaded farmer map data from the controller
    if (this.farmer?.farm_location_and_parcels) {
      // Delay setting farmer data to allow map to initialize properly
      this.$nextTick(() => {
        // Add a small delay to ensure the map component is fully initialized
        setTimeout(() => {
          // Check if component is still mounted before setting farmer data
          if (this._isDestroying || !this.$el) {
            return;
          }
          
          // Ensure the farmer data exists and has the required structure
          const farmerData = this.farmer.farm_location_and_parcels;
          if (farmerData && typeof farmerData === 'object') {
            // Ensure farms array exists
            if (!farmerData.farms || !Array.isArray(farmerData.farms)) {
              farmerData.farms = [];
            }
            
            this.$root.farmer = farmerData;
            this.$root.farmer.count = this.farmer.farms_count || 0;
            
            // Ensure zoom is enabled for task views to show parcels
            this.$root.farmer.zoom = true;
            
          } else {
            console.warn('Task View: farmer.farm_location_and_parcels is not valid:', farmerData);
          }
        }, 100); // Small delay to ensure map component is ready
      });
    } else {
      console.warn('Task View: No farmer.farm_location_and_parcels data found, trying alternative approach');
      
      // Fallback: Use the same approach as Farmer View - make API call
      this.loadMapForFarmer();
    }

    if (this.farmer?.organisation?.name == "ISEC") {
      //load LPIS Map Details
      this.$root.Lpis.source = "isec";
      this.$root.Lpis.herdNo = this.farmer.herd_no;
      this.$root.Lpis.showLpisAreaDetails = false; // this is use to hide the lpis area details
      this.$root.Lpis.filterCompletedHerdNo = true; //this is to prevent showing herd number toggle button
      this.$root.Lpis.show = true; // this is to show the lpis map
      
      // Enable zoom for sample paths on task views
      this.$root.paths.zoom = true;
      this.$root.paths.forceZoom = true; // Force zoom for task views regardless of farmer data state
      
      // Only show sampler and dropoff locations for ISEC
      this.$root.services.dropoff_points = true;
      this.$root.services.sampler_location = true;
    } else {
      // Linked parcels come with farm_location_and_parcels
      showDafmLpis(this.$root.Lpis);

      this.$root.paths.zoom = true;
      this.$root.paths.override = true;
      this.$root.paths.forceZoom = true; // Force zoom for task views regardless of farmer data state
      this.$root.services.dropoff_points = false;
      this.$root.services.sampler_location = false;
    }
    
    // Process samples to include georeference marker coordinates
    const samplesWithMarkers = this.samples ? this.samples.map(sample => ({
      ...sample,
      marker_coordinates: this.extractMarkerCoordinates(sample)
    })) : [];
    
    // Use nextTick to ensure the assignment happens after the component is fully reactive
    this.$nextTick(() => {
      // Force a reactive update by reassigning the entire paths object
      this.$root.paths = {
        ...this.$root.paths,
        samples: samplesWithMarkers
      };
    });
  },



  methods: {
    tabClass(name) {
      const isActive = this.tab === name;
      return [
        isActive
          ? 'bg-white text-navy-dark border-navy-dark shadow-sm'
          : 'text-navy border-transparent hover:text-navy-dark hover:border-navy-dark',
      ].join(' ');
    },

    toggleActions() {
      this.showActions = !this.showActions;
    },

    getStatusClass(status) {
      const statusClasses = {
        'not_started': 'bg-gray-100 text-gray-800',
        'in_progress': 'bg-blue-100 text-blue-800',
        'collected': 'bg-yellow-100 text-yellow-800',
        'lab': 'bg-indigo-100 text-indigo-800',
        'complete': 'bg-green-100 text-green-800',
        'completed': 'bg-green-100 text-green-800',
        'invoiced': 'bg-purple-100 text-purple-800',
        'rejected': 'bg-red-100 text-red-700',
        'cancelled': 'bg-red-100 text-red-800',
      };
      return statusClasses[status] || 'bg-gray-100 text-gray-800';
    },

    scroll(direction) {
      const scrollContainer = this.$refs.scrollContainer;
      const scrollAmount = 50; // Adjust scroll amount as needed

      if (direction === "left") {
        scrollContainer.scrollLeft -= scrollAmount;
      } else if (direction === "right") {
        scrollContainer.scrollLeft += scrollAmount;
      }
    },

    extractMarkerCoordinates(sample) {
      if (!sample.markers || !Array.isArray(sample.markers) || !sample.paths_lat_lon) {
        return [];
      }

      const paths = sample.paths_lat_lon[0] || [];
      const markerCoordinates = [];

      // Extract coordinates for each marker index
      sample.markers.forEach(markerIndex => {
        // Handle case where markerIndex might be an array with a single value
        const actualIndex = Array.isArray(markerIndex) ? markerIndex[0] : markerIndex;
        
        if (paths[actualIndex] && Array.isArray(paths[actualIndex]) && paths[actualIndex].length >= 2) {
          markerCoordinates.push({
            longitude: paths[actualIndex][0],
            latitude: paths[actualIndex][1],
            index: actualIndex,
            sample_id: sample.sample_id || sample.id
          });
        }
      });

      return markerCoordinates;
    },

    updateTestSentToLab(eventData) {
      if (eventData) {
        // Update the test in the main tests array (used by Records component)
        if (this.tests && Array.isArray(this.tests)) {
          const mainTestIndex = this.tests.findIndex(test => test.hashid === eventData.testHashid);
          if (mainTestIndex !== -1) {
            this.$set(this.tests[mainTestIndex], 'sent_to_lab', eventData.sent_to_lab);
            this.$set(this.tests[mainTestIndex], 'sent_to_lab_datetime', eventData.sent_to_lab_datetime);
          }
        }
        
        // Update the test in task.tests array (used by Actions component)
        if (this.task && this.task.tests && Array.isArray(this.task.tests)) {
          const taskTestIndex = this.task.tests.findIndex(test => test.hashid === eventData.testHashid);
          if (taskTestIndex !== -1) {
            this.$set(this.task.tests[taskTestIndex], 'sent_to_lab', eventData.sent_to_lab);
            this.$set(this.task.tests[taskTestIndex], 'sent_to_lab_datetime', eventData.sent_to_lab_datetime);
          }
        }
        
        // Force reactivity update for all child components
        this.$nextTick(() => {
          this.$forceUpdate();
        });
      }
    },

    loadMapForFarmer() {
      console.log('Task View: Loading map for farmer using API call', this.farmer_id);
      let vm = this;

      // Use the farmer_id from the task to load the map data
      axios
        .post(route("app.farmer.map.view", this.farmer_id), {
          view: "parcels",
        })
        .then(function (response) {
          // Check if component is still mounted before setting farmer data
          if (vm._isDestroying || !vm.$el) {
            return;
          }
          
          console.log('Task View: Received farmer map data:', response.data);
          vm.$root.farmer = response.data;
          vm.$root.farmer.zoom = true; // Enable zoom for task views
          vm.$root.showSearch = false;
          
          console.log('Task View: Set farmer data from API:', {
            farms: vm.$root.farmer.farms?.length || 0,
            parcels: vm.$root.farmer.farms?.reduce((total, farm) => total + (farm.parcels?.length || 0), 0) || 0,
            zoom: vm.$root.farmer.zoom
          });
        })
        .catch(function (error) {
          console.error('Task View: Error loading farmer map data:', error);
        });
    },
  },

  beforeUnmount() {
    // Mark component as being destroyed to prevent async operations from completing
    this._isDestroying = true;
    
    // Clear farmer data to trigger map cleanup when exiting task view
    this.$root.farmer = null;
  },

  computed: {
    canEditTask() {
      const user = this.$page.props.auth.user;
      const task = this.task;

      if (!user || !task) return false;

      if (user.role === 'admin') return true;

      if ((user.role === 'consultant' || user.role === 'consultant_user') &&
          user.organisation_id === task.organisation_id) {
          return true;
      }

      if (user.role === 'contractor' && user.id === task.assigned_to) {
          return true;
      }

      if (user.role === 'soil_sampler' && task.assigned_to === user.id) {
          return true;
      }

      return false;
    },

    commentCount() {
      if (!this.tests || !Array.isArray(this.tests)) return 0;
      return this.tests.filter(test => test.task_comment && test.task_comment.trim() !== '').length;
    },

    recordsCount() {
      return this.samples ? this.samples.length : 0;
    },

    collectionMethodText() {
      // Use the task's collection_method field if available
      if (this.task && this.task.collection_method) {
        return this.task.collection_method === 'mobile' ? 'Mobile App' : 'Manual Entry';
      }
      
      // Fallback: Check if any samples exist to determine collection method
      if (this.samples && this.samples.length > 0) {
        // If samples have paths_xy data, it's mobile collection
        const hasMobileData = this.samples.some(sample => 
          sample.paths_xy && Array.isArray(sample.paths_xy) && sample.paths_xy.length > 0
        );
        return hasMobileData ? 'Mobile App' : 'Manual Entry';
      }
      
      return 'Not Set';
    },

    collectionMethodClass() {
      const method = this.collectionMethodText;
      if (method === 'Mobile App') return 'bg-blue-100 text-blue-800';
      if (method === 'Manual Entry') return 'bg-purple-100 text-purple-800';
      return 'bg-gray-100 text-gray-800';
    },

    labStatusInfo() {
      if (!this.tests || !Array.isArray(this.tests) || this.tests.length === 0) return null;
      
      const totalTests = this.tests.length;
      
      // Get tests with lab status
      const testsWithStatus = this.tests.filter(test => test.lab_status && test.lab_status.trim() !== '');
      
      if (testsWithStatus.length === 0) return null;
      
      // If all tests have status, check if they're the same
      if (testsWithStatus.length === totalTests) {
        const uniqueStatuses = [...new Set(testsWithStatus.map(test => test.lab_status))];
        if (uniqueStatuses.length === 1) {
          return uniqueStatuses[0]; // All same status
        }
        return 'Mixed Status'; // All have status but different
      }
      
      // Some tests have status, some don't - show counter
      if (testsWithStatus.length === 1) {
        return `${testsWithStatus[0].lab_status} (1/${totalTests})`;
      }
      
      return `${testsWithStatus.length}/${totalTests} with Status`;
    },

    labStatusClass() {
      const status = this.labStatusInfo || 'N/A';
      
      // Single status cases
      if (['Created', 'Unknown', 'Registered', 'Scheduled'].includes(status)) {
        return 'bg-yellow-100 text-yellow-800';
      } else if (status === 'Testing') {
        return 'bg-blue-100 text-blue-800';
      } else if (['Results', 'Invoice', 'Checked', 'Certified'].includes(status)) {
        return 'bg-green-100 text-green-800';
      }
      
      // Multi-status cases
      else if (status === 'Mixed Status' || status.includes('Different')) {
        return 'bg-orange-100 text-orange-800';
      } else if (status.includes('with Status') || status.includes('/')) {
        return 'bg-blue-100 text-blue-800'; // Partial progress
      }
      
      return 'bg-gray-100 text-gray-800'; // Default for "N/A" and other cases
    },

    sentToLabInfo() {
      if (!this.tests || !Array.isArray(this.tests) || this.tests.length === 0) return null;
      
      const totalTests = this.tests.length;
      const testsWithSentDate = this.tests.filter(test => test.sent_to_lab_datetime);
      
      if (testsWithSentDate.length === 0) return null;
      
      // Show simple counter format
      return `${testsWithSentDate.length}/${totalTests}`;
    },

    getFirstFarmId() {
      // Try to get farm ID from various sources
      if (this.tests && this.tests.length > 0 && this.tests[0].farm_id) {
        return this.tests[0].farm_id;
      }
      
      if (this.task && this.task.tests && this.task.tests.length > 0 && this.task.tests[0].farm_id) {
        return this.task.tests[0].farm_id;
      }
      
      if (this.farmer && this.farmer.farms && this.farmer.farms.length > 0) {
        return this.farmer.farms[0].id;
      }
      
      // Fallback - this should not happen in normal cases
      return null;
    },

    getTaskLabId() {
      // Map lab names to IDs - this should match the available labs
      const labNameToId = {
        'FBA Labs': 1,
        'Dairygold': 2,
        'Southern Scientific': 3,
        'IAS': 4
      };
      
      if (this.task && this.task.lab) {
        return labNameToId[this.task.lab] || null;
      }
      
      // Fallback: get from first test if available
      if (this.tests && this.tests.length > 0 && this.tests[0].lab) {
        return labNameToId[this.tests[0].lab_name] || this.tests[0].lab || null;
      }
      
      return null;
    }
  }
};
</script>
